/**
 * The calls of the dispatch API, one function per route (paths under `/api/v1/inventory` once T15 registers the plugins).
 * Nothing here decides who may call: the hooks fence every call with the owner and the permissions, and the server decides again.
 */

import { api } from '@/lib/api';
import type {
  CursorPage,
  DispatchCancelResult,
  DispatchChannel,
  DispatchEnqueueBody,
  DispatchEnqueueResult,
  DispatchHistoryPage,
  DispatchPreview,
  DispatchResolveResult,
  DispatchStatus,
  DispatchSummary,
  ResolveDispatchBody,
  SupplierDispatchSettings,
  SupplierDispatchSettingsBody,
} from './dispatchTypes';

const BASE = '/inventory';

/** Bounded pages: a page is small, and only so many are ever held (see `MAX_LOADED_PAGES`). */
export const LIST_PAGE_SIZE = 25;
export const HISTORY_PAGE_SIZE = 50;
export const MAX_LOADED_PAGES = 10;

const order = (purchaseOrderId: string): string =>
  `${BASE}/purchase-orders/${encodeURIComponent(purchaseOrderId)}`;
const dispatchPath = (purchaseOrderId: string, dispatchId: string): string =>
  `${order(purchaseOrderId)}/dispatches/${encodeURIComponent(dispatchId)}`;
const settingsPath = (supplierId: string): string =>
  `${BASE}/suppliers/${encodeURIComponent(supplierId)}/dispatch-settings`;

export function fetchOrderDispatches(
  purchaseOrderId: string,
  cursor: string | null
): Promise<CursorPage<DispatchSummary>> {
  return api.get<CursorPage<DispatchSummary>>(`${order(purchaseOrderId)}/dispatches`, {
    params: { limit: LIST_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
  });
}

export interface TenantDispatchFilters {
  status?: DispatchStatus | undefined;
  purchaseOrderId?: string | undefined;
}

export function fetchTenantDispatches(
  filters: TenantDispatchFilters,
  cursor: string | null
): Promise<CursorPage<DispatchSummary>> {
  return api.get<CursorPage<DispatchSummary>>(`${BASE}/purchase-order-dispatches`, {
    params: {
      limit: LIST_PAGE_SIZE,
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.purchaseOrderId ? { purchaseOrderId: filters.purchaseOrderId } : {}),
      ...(cursor ? { cursor } : {}),
    },
  });
}

export function fetchDispatchSummary(
  purchaseOrderId: string,
  dispatchId: string
): Promise<DispatchSummary> {
  return api.get<DispatchSummary>(dispatchPath(purchaseOrderId, dispatchId));
}

export function fetchDispatchHistory(
  purchaseOrderId: string,
  dispatchId: string,
  cursor: string | null
): Promise<DispatchHistoryPage> {
  return api.get<DispatchHistoryPage>(`${dispatchPath(purchaseOrderId, dispatchId)}/history`, {
    params: { limit: HISTORY_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
  });
}

/** Side-effect free on the server: the answer is the frozen document, the recipient and the hash to review. */
export function postDispatchPreview(
  purchaseOrderId: string,
  channel: DispatchChannel
): Promise<DispatchPreview> {
  return api.post<DispatchPreview>(`${order(purchaseOrderId)}/dispatch/preview`, { channel });
}

/** The body is sent exactly as given: the channel, the operation key and the reviewed hash. */
export function postDispatchEnqueue(
  purchaseOrderId: string,
  body: DispatchEnqueueBody
): Promise<DispatchEnqueueResult> {
  return api.post<DispatchEnqueueResult>(`${order(purchaseOrderId)}/dispatch`, body);
}

export function postDispatchCancel(
  purchaseOrderId: string,
  dispatchId: string,
  reason: string
): Promise<DispatchCancelResult> {
  return api.post<DispatchCancelResult>(`${dispatchPath(purchaseOrderId, dispatchId)}/cancel`, {
    reason,
  });
}

export function postDispatchResolve(
  purchaseOrderId: string,
  dispatchId: string,
  body: ResolveDispatchBody
): Promise<DispatchResolveResult> {
  return api.post<DispatchResolveResult>(
    `${dispatchPath(purchaseOrderId, dispatchId)}/resolve`,
    body
  );
}

export function fetchSupplierDispatchSettings(
  supplierId: string
): Promise<SupplierDispatchSettings> {
  return api.get<SupplierDispatchSettings>(settingsPath(supplierId));
}

export function putSupplierDispatchSettings(
  supplierId: string,
  body: SupplierDispatchSettingsBody
): Promise<SupplierDispatchSettings> {
  return api.put<SupplierDispatchSettings>(settingsPath(supplierId), body);
}
